/**
 * @file EscalacaoModal.js
 * @description Modal que exibe a escalação de um time no Cartola da Copa,
 * com os titulares dispostos num campo por posição (estilo Cartola FC) e as
 * reservas em lista. Mostra a rodada atual ou a anterior quando o mercado
 * ainda está aberto (decisão feita no backend), com parciais ao vivo.
 */
import { useEffect, useState } from "react";
import { X, Loader2, AlertTriangle, Shirt, Repeat, Armchair, ArrowUp, ArrowDown } from "lucide-react";
import { getEscalacao, getEscudoUrl } from "../api/ligaApi";

// Cache em memória das escalações já vistas: reabrir o mesmo time é
// instantâneo. TTL curto porque as parciais mudam durante a rodada.
const escalacaoCache = new Map();
const ESCALACAO_TTL = 60 * 1000;

const FotoAtleta = ({ atleta, className }) => (
  <div className={`rounded-full bg-campo-800 overflow-hidden ${className}`}>
    {atleta.foto && (
      <img
        src={getEscudoUrl(atleta.foto)}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    )}
  </div>
);

const BadgeCapitao = ({ className }) => (
  <span
    className={`flex items-center justify-center w-4 h-4 rounded-full bg-ouro text-white text-[9px] font-black ${className}`}
    title="Capitão"
  >
    C
  </span>
);

const BadgeReservaLuxo = ({ className }) => (
  <span
    className={`flex items-center justify-center w-4 h-4 rounded-full bg-ouro/20 ${className}`}
    title="Reserva de Luxo"
  >
    <Repeat size={10} className="text-ouro" strokeWidth={2.5} />
  </span>
);

// Seta de substituição: verde para cima (reserva de luxo acionado / entrou) ou
// vermelha para baixo (titular substituído / desceu pro banco). Funciona ao
// vivo e na rodada fechada — em cada caso o jogador está no campo ou no banco.
const SetaSubstituicao = ({ tipo, className = "" }) => {
  const entrou = tipo === "entrou";
  const Icon = entrou ? ArrowUp : ArrowDown;
  return (
    <span
      title={entrou ? "Acionado (reserva de luxo)" : "Substituído pelo reserva de luxo"}
      className={`flex items-center justify-center w-4 h-4 rounded-full ring-2 ring-campo-900 shadow ${
        entrou ? "bg-emerald-500" : "bg-red-500"
      } ${className}`}
    >
      <Icon size={10} className="text-white" strokeWidth={3} />
    </span>
  );
};

// Renderiza a seta correta para o atleta (entrou / saiu / nenhuma)
const SetaDoAtleta = ({ atleta, className }) => {
  if (atleta.acionado) return <SetaSubstituicao tipo="entrou" className={className} />;
  if (atleta.substituido) return <SetaSubstituicao tipo="saiu" className={className} />;
  return null;
};

// Pontuação exibida no card. O capitão mostra a pontuação com o multiplicador
// de 1,5x (só na exibição — o cálculo do total já aplica o multiplicador por
// conta própria no backend).
const pontosDoAtleta = (atleta, mostrarPontos) => {
  if (!(mostrarPontos && atleta.entrou_em_campo)) return "–";
  const base = atleta.pontos ?? 0;
  const pts = atleta.is_capitao ? base * 1.5 : base;
  return pts.toFixed(2);
};

// Jogador posicionado no campo: foto com badges, nome e pontuação
const JogadorCampo = ({ atleta, mostrarPontos }) => {
  const pontos = pontosDoAtleta(atleta, mostrarPontos);
  return (
    <div className="flex flex-col items-center w-14 sm:w-16">
      <div className="relative">
        <FotoAtleta
          atleta={atleta}
          className="w-10 h-10 sm:w-12 sm:h-12 border-2 border-white/25 shadow-md"
        />
        <SetaDoAtleta
          atleta={atleta}
          className="absolute -left-1.5 top-1/2 -translate-y-1/2 z-10"
        />
        {atleta.is_capitao && (
          <BadgeCapitao className="absolute -top-1 -right-1 ring-2 ring-emerald-900" />
        )}
        {atleta.clube && atleta.clube.escudo && (
          <img
            src={getEscudoUrl(atleta.clube.escudo)}
            alt={atleta.clube.nome}
            loading="lazy"
            className="absolute -bottom-0.5 -right-1 w-4 h-4 drop-shadow"
          />
        )}
      </div>
      <span className="mt-1 w-full text-center truncate text-[10px] font-semibold text-white bg-black/60 px-1.5 py-0.5 rounded-t leading-tight">
        {atleta.apelido}
      </span>
      <span
        className={`w-full text-center text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-b leading-tight ${
          pontos !== "–" ? "bg-ouro text-white" : "bg-black/40 text-white/50"
        }`}
      >
        {pontos}
      </span>
    </div>
  );
};

// Campo vertical com os titulares por posição: ATA, MEI, defesa e GOL,
// técnico no canto, como no Cartola
const CampoEscalacao = ({ atletas, mostrarPontos }) => {
  const porPosicao = (id) => atletas.filter((a) => a.posicao_id === id);
  const laterais = porPosicao(2);
  const zagueiros = porPosicao(3);
  // Laterais abrem pelos flancos, zagueiros por dentro
  const defesa =
    laterais.length === 2
      ? [laterais[0], ...zagueiros, laterais[1]]
      : [...laterais, ...zagueiros];

  const linhas = [
    porPosicao(5), // atacantes
    porPosicao(4), // meias
    defesa,
    porPosicao(1), // goleiro
  ].filter((linha) => linha.length > 0);

  const tecnico = atletas.find((a) => a.posicao_id === 6);

  return (
    <div className="relative mx-3 sm:mx-4 mt-3 rounded-xl overflow-hidden bg-gradient-to-b from-emerald-800 via-emerald-900 to-emerald-950 border border-emerald-700/40">
      {/* Faixas do gramado */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 44px, transparent 44px 88px)",
        }}
      />
      {/* Marcações do campo */}
      <div className="absolute inset-x-3 inset-y-2 border border-white/15 rounded-sm pointer-events-none" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border border-white/15 rounded-full pointer-events-none" />
      <div className="absolute inset-x-3 top-1/2 border-t border-white/15 pointer-events-none" />
      <div className="absolute left-1/2 -translate-x-1/2 top-2 w-32 h-10 border border-t-0 border-white/15 pointer-events-none" />
      <div className="absolute left-1/2 -translate-x-1/2 bottom-2 w-32 h-10 border border-b-0 border-white/15 pointer-events-none" />

      {/* Linhas de jogadores */}
      <div className="relative flex flex-col justify-between gap-3 py-4 min-h-[22rem]">
        {linhas.map((linha, i) => (
          <div key={i} className="flex justify-evenly items-start px-2">
            {linha.map((atleta) => (
              <JogadorCampo
                key={atleta.atleta_id}
                atleta={atleta}
                mostrarPontos={mostrarPontos}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Técnico no canto do campo */}
      {tecnico && (
        <div className="absolute bottom-2.5 left-2.5">
          <JogadorCampo atleta={tecnico} mostrarPontos={mostrarPontos} />
        </div>
      )}
    </div>
  );
};

// Linha do banco de reservas, com visual alinhado ao campo
const LinhaReserva = ({ atleta, mostrarPontos }) => {
  const pontos = pontosDoAtleta(atleta, mostrarPontos);
  return (
    <div
      className={`flex items-center gap-2.5 px-3 py-2 ${
        atleta.is_reserva_luxo ? "bg-ouro/[0.06]" : ""
      }`}
    >
      {/* Posição */}
      <span className="w-9 text-center text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-1 py-1 flex-shrink-0">
        {atleta.posicao}
      </span>

      {/* Foto com a bandeirinha da seleção, igual ao campo */}
      <div className="relative flex-shrink-0">
        <FotoAtleta
          atleta={atleta}
          className="w-9 h-9 border border-campo-700"
        />
        <SetaDoAtleta
          atleta={atleta}
          className="absolute -left-1.5 top-1/2 -translate-y-1/2 z-10"
        />
        {atleta.clube && atleta.clube.escudo && (
          <img
            src={getEscudoUrl(atleta.clube.escudo)}
            alt={atleta.clube.nome}
            loading="lazy"
            className="absolute -bottom-0.5 -right-1 w-4 h-4 drop-shadow"
          />
        )}
      </div>

      {/* Nome + seleção */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-tinta text-sm truncate flex items-center gap-1.5">
          <span className="truncate">{atleta.apelido}</span>
          {atleta.is_reserva_luxo && <BadgeReservaLuxo className="flex-shrink-0" />}
        </p>
        <p className="text-[11px] text-gray-500 truncate">
          {atleta.is_reserva_luxo ? (
            <span className="text-ouro/80 font-semibold">Reserva de Luxo</span>
          ) : (
            atleta.clube && atleta.clube.nome
          )}
        </p>
      </div>

      {/* Pontuação em pílula, como no campo */}
      <span
        className={`min-w-[3.25rem] text-center text-xs font-bold tabular-nums px-2 py-1 rounded-md flex-shrink-0 ${
          pontos !== "–"
            ? "bg-ouro text-white"
            : "bg-black/40 text-white/40"
        }`}
      >
        {pontos}
      </span>
    </div>
  );
};

const EscalacaoModal = ({ time, rodada, onClose }) => {
  const [escalacao, setEscalacao] = useState(null);
  const [erro, setErro] = useState(null);

  // Trava o scroll do body enquanto o modal está aberto
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    let ativo = true;
    setErro(null);

    // chave separada no modo demo para não reaproveitar o cache normal
    const demo =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("demo") === "1";
    const chave = `${time.cartola_time_id}:${rodada ?? "atual"}${demo ? ":demo" : ""}`;

    const emCache = escalacaoCache.get(chave);
    if (emCache && Date.now() - emCache.fetchedAt < ESCALACAO_TTL) {
      setEscalacao(emCache.data);
      return undefined;
    }
    setEscalacao(null);
    getEscalacao(time.cartola_time_id, rodada)
      .then((dados) => {
        escalacaoCache.set(chave, {
          data: dados,
          fetchedAt: Date.now(),
        });
        if (ativo) setEscalacao(dados);
      })
      .catch((e) => ativo && setErro(e.message));
    return () => {
      ativo = false;
    };
  }, [time.cartola_time_id, rodada]);

  // Pontos dos atletas só fazem sentido depois que a bola rolou na rodada
  const mostrarPontos =
    !!escalacao &&
    (escalacao.pontos != null ||
      escalacao.atletas.some((a) => a.entrou_em_campo));

  return (
    <div
      className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex justify-center items-end sm:items-center p-0 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-campo-900 border border-campo-700 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[85vh] sm:max-h-[88vh] flex flex-col overflow-hidden">
        {/* Cabeçalho: escudo, time e cartoleiro */}
        <div className="p-4 sm:p-5 border-b border-campo-700/50 bg-campo-800/50 flex items-center gap-3 flex-shrink-0">
          <img
            src={getEscudoUrl(
              (escalacao && escalacao.time.escudo_url) || time.escudo_url
            )}
            alt=""
            className="w-12 h-12 rounded-full flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-tinta leading-tight truncate">
              {time.nome_time}
            </h3>
            <p className="text-xs text-gray-400 truncate">
              {time.nome_cartoleiro}
            </p>
            {escalacao && (
              <p className="text-[11px] text-gray-500 mt-0.5">
                Rodada {escalacao.rodada}
                {escalacao.esquema ? ` • ${escalacao.esquema}` : ""}
                {escalacao.pontos == null && " • em andamento"}
              </p>
            )}
          </div>
          {escalacao && (escalacao.pontos != null || escalacao.parcial) && (
            <div className="text-right flex-shrink-0">
              <p className="text-xl font-black text-ouro tabular-nums">
                {(escalacao.pontos ?? escalacao.pontos_parcial ?? 0).toFixed(2)}
              </p>
              <p className="text-[10px] text-gray-500 -mt-0.5">
                {escalacao.pontos != null ? "pts" : "parcial"}
              </p>
            </div>
          )}
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-campo-700 hover:text-tinta transition-colors self-start"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="overflow-y-auto flex-1 pb-3">
          {erro ? (
            <div className="text-center py-12 px-5">
              <AlertTriangle size={28} className="text-red-400 mx-auto mb-3" />
              <p className="text-tinta-soft text-sm">{erro}</p>
            </div>
          ) : !escalacao ? (
            <div className="flex items-center justify-center gap-2 py-14 text-gray-400 text-sm">
              <Loader2 size={18} className="animate-spin" />
              Buscando escalação...
            </div>
          ) : escalacao.atletas.length === 0 ? (
            <div className="text-center py-12 px-5">
              <Shirt size={28} className="text-campo-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">
                O cartoleiro não escalou time nesta rodada.
              </p>
            </div>
          ) : (
            <>
              <CampoEscalacao
                atletas={escalacao.atletas}
                mostrarPontos={mostrarPontos}
              />

              {escalacao.reservas.length > 0 && (
                <div className="mx-3 sm:mx-4 mt-3 rounded-xl border border-campo-700/50 bg-campo-950/60 overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-campo-700/40 bg-campo-800/40">
                    <Armchair size={13} className="text-gray-400" />
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Banco de Reservas
                    </span>
                  </div>
                  <div className="divide-y divide-campo-700/30">
                    {escalacao.reservas.map((atleta) => (
                      <LinhaReserva
                        key={atleta.atleta_id}
                        atleta={atleta}
                        mostrarPontos={mostrarPontos}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EscalacaoModal;
